import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';

const FeesContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const CurrentMonthCard = styled.div<{ paid?: boolean }>`
  background: ${props => props.paid ? '#d1e7dd' : '#f8d7da'};
  border: 2px solid ${props => props.paid ? '#badbcc' : '#f5c2c7'};
  padding: 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CurrentMonthTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.75rem;
`;

const CurrentMonthStatus = styled.div<{ paid?: boolean }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.paid ? '#0f5132' : '#842029'};
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const CurrentMonthMessage = styled.div<{ paid?: boolean }>`
  font-size: 0.9375rem;
  color: ${props => props.paid ? '#0f5132' : '#842029'};
  line-height: 1.6;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const PaymentHistory = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const HistoryTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }
`;

const MonthCard = styled.div<{ paid?: boolean; isCurrent?: boolean }>`
  padding: 1.25rem;
  border: 2px solid ${props => {
    if (props.isCurrent) return '#000000';
    if (props.paid) return '#d1e7dd';
    return '#dee2e6';
  }};
  border-radius: 12px;
  background: ${props => {
    if (props.isCurrent) return '#f8f9fa';
    if (props.paid) return '#f8fff9';
    return 'white';
  }};
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #000000;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const MonthLabel = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const MonthStatus = styled.div<{ paid?: boolean }>`
  font-size: 2rem;
  color: ${props => props.paid ? '#0f5132' : '#dc3545'};
  margin-bottom: 0.25rem;
`;

const MonthInfo = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.4;
`;

const UnpaidInfo = styled.div`
  font-size: 0.7rem;
  color: #6c757d;
  margin-top: 0.25rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.9375rem;
  color: #6c757d;
  font-weight: 600;
`;

const DetailValue = styled.span`
  font-size: 0.9375rem;
  color: #000000;
  font-weight: 500;
`;

const Toast = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: ${props => props.show ? '2rem' : '-100px'};
  left: 50%;
  transform: translateX(-50%);
  background: #000000;
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  transition: bottom 0.3s ease;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    bottom: ${props => props.show ? '1.5rem' : '-100px'};
    padding: 0.875rem 1.25rem;
    font-size: 0.875rem;
  }
`;

interface PaymentDetail {
  amount: string;
  paymentDate: string;
  memo: string;
}

interface PaymentData {
  [month: string]: PaymentDetail | null;
}

const MemberFees: React.FC = () => {
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const currentMonth = new Date().getMonth() + 1; // 1-12
  
  // 계좌 정보
  const BANK_ACCOUNT = '카카오뱅크 79423097529 추도빈';
  const [payments, setPayments] = useState<PaymentData>({});
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
 
  // 내 회비 납부 내역 조회
  const fetchMyFees = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/fees/my-fees?year=${currentYear}`);
      if (response.data.success) {
        // API 응답을 PaymentData 형식으로 변환
        const feeData: PaymentData = {};
        
        // 모든 월을 null로 초기화
        for (let i = 1; i <= 12; i++) {
          feeData[i.toString()] = null;
        }
        
        // 납부 내역이 있는 월만 업데이트
        response.data.data.forEach((payment: any) => {
          feeData[payment.month.toString()] = {
            amount: payment.amount?.toString() || '0',
            paymentDate: payment.date || '',
            memo: payment.memo || ''
          };
        });
        
        setPayments(feeData);
      }
    } catch (error) {
      console.error('회비 내역 조회 실패:', error);
      // 에러 발생 시 모든 월을 null로 초기화
      const emptyData: PaymentData = {};
      for (let i = 1; i <= 12; i++) {
        emptyData[i.toString()] = null;
      }
      setPayments(emptyData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyFees();
  }, []);

  const months = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  
  const currentMonthPaid = payments[currentMonth.toString()] !== null;
  
  // 통계
  const stats = {
    paid: months.filter(m => payments[m] !== null).length,
    unpaid: months.filter(m => payments[m] === null).length,
    total: months.filter(m => payments[m] !== null).reduce((sum, m) => {
      const amount = payments[m]?.amount;
      return sum + (amount ? parseInt(amount) : 0);
    }, 0),
  };

  const handleMonthClick = (month: string) => {
    if (payments[month]) {
      // 납부 완료된 경우: 상세 정보 모달 표시
      setSelectedMonth(month);
    } else {
      // 미납인 경우: 계좌번호 복사
      copyToClipboard(BANK_ACCOUNT);
    }
  };

  const copyToClipboard = (text: string) => {
    // 최신 브라우저용
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        showToastMessage();
      }).catch(err => {
        console.error('클립보드 복사 실패:', err);
        fallbackCopyToClipboard(text);
      });
    } else {
      // 구형 브라우저 또는 HTTP 환경용 fallback
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      showToastMessage();
    } catch (err) {
      console.error('계좌번호 복사 실패:', err);
      alert(`계좌번호: ${text}\n\n수동으로 복사해주세요.`);
    }
    
    document.body.removeChild(textArea);
  };

  const showToastMessage = () => {
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 2000); // 2초 후 토스트 사라짐
  };

  if (loading) {
    return (
      <DashboardLayout title="회비">
        <FeesContainer>
          <Title>회비</Title>
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>로딩 중...</div>
        </FeesContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="회비">
      <FeesContainer>
        <Title>회비</Title>
        
        <CurrentMonthCard paid={currentMonthPaid}>
          <CurrentMonthTitle>이번 달 ({currentMonth}월) 회비</CurrentMonthTitle>
          <CurrentMonthStatus paid={currentMonthPaid}>
            {currentMonthPaid ? '✓ 납부 완료' : '✕ 미납'}
          </CurrentMonthStatus>
          <CurrentMonthMessage paid={currentMonthPaid}>
            {currentMonthPaid 
              ? `${payments[currentMonth.toString()]?.paymentDate}에 ${parseInt(payments[currentMonth.toString()]?.amount || '0').toLocaleString()}원 납부` 
              : '아직 이번 달 회비를 납부하지 않으셨습니다.'}
          </CurrentMonthMessage>
        </CurrentMonthCard>
        
        <StatsRow>
          <StatCard>
            <StatLabel>납부</StatLabel>
            <StatValue>{stats.paid}개월</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>미납</StatLabel>
            <StatValue>{stats.unpaid}개월</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>총 납부액</StatLabel>
            <StatValue>{stats.total.toLocaleString()}원</StatValue>
          </StatCard>
        </StatsRow>
        
        <PaymentHistory>
          <HistoryTitle>월별 납부 내역</HistoryTitle>
          
          <MonthGrid>
            {months.map(month => {
              const isPaid = payments[month] !== null;
              const isCurrent = parseInt(month) === currentMonth;
              return (
                <MonthCard
                  key={month}
                  paid={isPaid}
                  isCurrent={isCurrent}
                  onClick={() => handleMonthClick(month)}
                >
                  <MonthLabel>{month}월</MonthLabel>
                  <MonthStatus paid={isPaid}>
                    {isPaid ? '✓' : '✕'}
                  </MonthStatus>
                  {isPaid ? (
                    <MonthInfo>
                      {payments[month]?.paymentDate}
                      <br />
                      {parseInt(payments[month]?.amount || '0').toLocaleString()}원
                    </MonthInfo>
                  ) : (
                    <UnpaidInfo>클릭하여<br />계좌 복사</UnpaidInfo>
                  )}
                </MonthCard>
              );
            })}
          </MonthGrid>
        </PaymentHistory>
        
        {/* 납부 상세 정보 모달 */}
        <ModalOverlay 
          isOpen={selectedMonth !== null}
          onClick={() => setSelectedMonth(null)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedMonth}월 납부 상세</ModalTitle>
              <CloseButton onClick={() => setSelectedMonth(null)}>×</CloseButton>
            </ModalHeader>
            
            {selectedMonth && payments[selectedMonth] && (
              <>
                <DetailRow>
                  <DetailLabel>월</DetailLabel>
                  <DetailValue>{selectedMonth}월</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>입금액</DetailLabel>
                  <DetailValue>{parseInt(payments[selectedMonth]?.amount || '0').toLocaleString()}원</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>납부일</DetailLabel>
                  <DetailValue>{payments[selectedMonth]?.paymentDate}</DetailValue>
                </DetailRow>
                {payments[selectedMonth]?.memo && (
                  <DetailRow>
                    <DetailLabel>메모</DetailLabel>
                    <DetailValue>{payments[selectedMonth]?.memo}</DetailValue>
                  </DetailRow>
                )}
              </>
            )}
          </ModalContent>
        </ModalOverlay>
        
        {/* 토스트 메시지 */}
        <Toast show={showToast}>
          계좌번호가 복사되었습니다
        </Toast>
      </FeesContainer>
    </DashboardLayout>
  );
};

export default MemberFees;

